// EU-compliant consent management. Opt-in by default: until the user
// clicks "Accept", no analytics or marketing SDKs load.
//
// The choice is persisted across THREE layers so it survives environments
// that block or partition web storage (notably the in-app browsers used by
// iOS Mail/Messages/Gmail when opening a notification deep link):
//   1. in-memory  — always writable; guarantees a remount within the same JS
//                   context never re-shows the banner even if 2 and 3 fail.
//   2. cookie     — a first-party cookie; survives a reload/redirect (the auth
//                   flow a `/messages/{id}` deep link triggers) and is usually
//                   allowed even when localStorage is partitioned.
//   3. localStorage — original store, kept for backwards compatibility.
//   4. sessionStorage — Task #8007: survives same-tab HARD navigations (e.g.
//                   a Cloudflare challenge or auth redirect) in webviews that
//                   block cookies AND localStorage but still allow per-tab
//                   session storage. Without it, any full-document reload in
//                   such a context wiped the in-memory choice and re-showed
//                   the banner — the "can't get past the cookie window" loop.
// Reads consult them in that order; writes fan out to all of them. Listeners
// can subscribe to changes.

export type ConsentCategory = "essential" | "analytics" | "marketing";
export type ConsentState = Record<ConsentCategory, boolean> & {
  ts: number;
  // Task #6837 (G) — records whether Global Privacy Control was active when
  // the choice was stored. Optional and backward-compatible: older persisted
  // states parse fine without it.
  gpc?: boolean;
};

// Task #6837 (G) — Global Privacy Control. When the browser advertises
// `navigator.globalPrivacyControl === true` we treat it as a legally binding
// opt-out from marketing/advertising: marketing consent is forced off even on
// an explicit "Accept all", and the applied GPC state is recorded alongside
// the stored consent.
export function isGpcActive(): boolean {
  if (typeof navigator === "undefined") return false;
  try {
    return (navigator as any).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

export const CONSENT_KEY =
  (import.meta.env.VITE_CONSENT_COOKIE_NAME as string | undefined) ||
  "bac_consent_v1";

const DEFAULT_CONSENT: ConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  ts: 0,
};

// One year — the consent choice should outlive a browsing session.
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

// In-memory session cache. This is the only store guaranteed to be writable,
// so tapping a choice always "sticks" for the life of the JS context even when
// both durable layers are blocked, and a remount never re-shows the banner.
let memoryConsent: ConsentState | null = null;
let memoryChosen = false;

// Result of the most recent persistence attempt. `memory` is always true (it
// cannot fail), so a within-session dismiss always sticks. When BOTH
// `localStorage` and `cookieReadback` are false, no durable layer accepted the
// choice — the banner WILL re-appear on a full reload. That is precisely the
// in-app-webview dead-end this task guards against, so we expose the signal for
// diagnostics rather than swallowing it silently.
export type ConsentPersistDiagnostics = {
  memory: boolean;
  localStorage: boolean;
  cookie: boolean;
  cookieReadback: boolean;
  // Task #8007 — per-tab fallback layer; true when the sessionStorage write
  // succeeded. Not durable across tabs/sessions, but survives same-tab hard
  // navigations when both durable layers are blocked.
  sessionStorage: boolean;
  // Task #6095 — account layer outcome: null = pending/unavailable,
  // true/false = the async server write succeeded/failed. Updated after the
  // registered account-sync promise settles.
  account: boolean | null;
};
let lastPersist: ConsentPersistDiagnostics | null = null;

export function getConsentPersistDiagnostics(): ConsentPersistDiagnostics | null {
  return lastPersist;
}

const listeners = new Set<(s: ConsentState) => void>();

// ── Task #6095 — account-level consent layer ────────────────────────────
// For logged-in users whose browser blocks BOTH cookies and localStorage
// (iOS "Block All Cookies" / Lockdown Mode) the account is the only durable
// store. AuthProvider registers a sync function once a session exists;
// setConsent() fires it (fire-and-forget) after the local layers, and the
// banner can await the outcome via waitForAccountPersist() to decide whether
// the "durable persistence unavailable" canary should still fire.
export type AccountConsentState = {
  analytics: boolean;
  marketing: boolean;
  ts: number;
};
type AccountConsentSyncFn = (s: ConsentState) => Promise<boolean>;
let accountSync: AccountConsentSyncFn | null = null;
let lastAccountPersist: Promise<boolean> = Promise.resolve(false);

export function registerAccountConsentSync(fn: AccountConsentSyncFn | null): void {
  accountSync = fn;
}

export function hasAccountConsentSync(): boolean {
  return accountSync !== null;
}

// Resolves with the outcome of the most recent account write attempt
// (false when no account layer is registered or no write happened yet).
export function waitForAccountPersist(): Promise<boolean> {
  return lastAccountPersist;
}

/**
 * Apply an account-stored consent choice fetched after login. Precedence:
 * a browser-stored choice with a newer-or-equal `ts` wins and the account
 * copy is ignored (returns false). Otherwise the account choice is applied
 * in-memory first, fanned out to the durable layers best-effort, and
 * listeners fire so consent-gated SDK loading reacts — WITHOUT echoing the
 * state back to the account (it came from there).
 * Returns true when the account choice was applied.
 */
export function hydrateConsentFromAccount(remote: AccountConsentState): boolean {
  if (typeof window === "undefined") return false;
  const remoteTs = Number(remote.ts) || 0;
  if (hasChosen() && getConsent().ts >= remoteTs) return false;
  const next: ConsentState = {
    essential: true,
    analytics: !!remote.analytics,
    marketing: !!remote.marketing,
    ts: remoteTs || Date.now(),
  };
  memoryConsent = next;
  memoryChosen = true;
  const serialized = JSON.stringify(next);
  writeLocalStorage(serialized);
  writeCookie(serialized);
  writeSessionStorage(serialized);
  for (const l of listeners) {
    try {
      l(next);
    } catch {}
  }
  return true;
}

function parseConsent(raw: string | null | undefined): ConsentState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const state: ConsentState = {
      essential: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      ts: parsed.ts || 0,
    };
    if (typeof parsed.gpc === "boolean") state.gpc = parsed.gpc;
    return state;
  } catch {
    return null;
  }
}

function readLocalStorage(): string | null {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

function writeLocalStorage(value: string): boolean {
  try {
    localStorage.setItem(CONSENT_KEY, value);
    return true;
  } catch {
    return false;
  }
}

// Task #8007 — sessionStorage layer. Same swallowing semantics as the other
// stores: a blocked write returns false and the caller carries on.
function readSessionStorage(): string | null {
  try {
    return sessionStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

function writeSessionStorage(value: string): boolean {
  try {
    sessionStorage.setItem(CONSENT_KEY, value);
    return true;
  } catch {
    return false;
  }
}

function readCookie(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const prefix = `${CONSENT_KEY}=`;
    for (const part of document.cookie.split(";")) {
      const c = part.trim();
      if (c.startsWith(prefix))
        return decodeURIComponent(c.slice(prefix.length));
    }
  } catch {}
  return null;
}

function writeCookie(value: string): boolean {
  if (typeof document === "undefined") return false;
  try {
    const secure =
      typeof window !== "undefined" && window.location?.protocol === "https:"
        ? "; Secure"
        : "";
    document.cookie = `${CONSENT_KEY}=${encodeURIComponent(
      value,
    )}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
    return true;
  } catch {
    return false;
  }
}

// Resolve the stored choice from the durable layers (localStorage → cookie)
// and hydrate the in-memory cache so subsequent reads are consistent.
function loadFromDurableStores(): ConsentState | null {
  const localRaw = readLocalStorage();
  const fromLocal = parseConsent(localRaw);
  // Task #8007 — sessionStorage is the last-resort read: only consulted when
  // both durable layers are empty/blocked, so a stale per-tab copy can never
  // shadow a fresher durable choice.
  const stored =
    fromLocal ??
    parseConsent(readCookie()) ??
    parseConsent(readSessionStorage());
  if (stored) {
    memoryConsent = stored;
    memoryChosen = true;
    // One-time backfill: visitors who chose consent before the cookie layer
    // existed have their choice ONLY in localStorage. Mirror it into the
    // cookie so it survives in-app browsers that partition/block web storage.
    // We write the original serialized value (preserving `ts`) rather than
    // routing through setConsent, so the timestamp isn't reset and listeners
    // aren't fired for what is an unchanged choice.
    if (fromLocal && localRaw && readCookie() === null) {
      writeCookie(localRaw);
    }
  }
  return stored;
}

export function getConsent(): ConsentState {
  if (typeof window === "undefined") return DEFAULT_CONSENT;
  if (memoryConsent) return memoryConsent;
  return loadFromDurableStores() ?? DEFAULT_CONSENT;
}

export function hasChosen(): boolean {
  if (typeof window === "undefined") return false;
  if (memoryChosen) return true;
  return loadFromDurableStores() !== null;
}

export function setConsent(
  partial: Partial<Omit<ConsentState, "ts">>,
): ConsentState {
  const current = getConsent();
  // Task #6837 (G) — GPC is a binding opt-out from marketing/advertising, so
  // marketing consent is forced off whenever GPC is active, even if the caller
  // (e.g. "Accept all") requested it. The applied GPC state is recorded.
  const gpc = isGpcActive();
  const next: ConsentState = {
    essential: true,
    analytics: partial.analytics ?? current.analytics,
    marketing: gpc ? false : (partial.marketing ?? current.marketing),
    ts: Date.now(),
    gpc,
  };
  // In-memory first — this cannot fail, so the choice always takes effect for
  // the current context (the banner closes and stays closed) even if every
  // durable store below is blocked.
  memoryConsent = next;
  memoryChosen = true;
  const serialized = JSON.stringify(next);
  const localOk = writeLocalStorage(serialized);
  const cookieOk = writeCookie(serialized);
  const sessionOk = writeSessionStorage(serialized);
  const persist: ConsentPersistDiagnostics = {
    memory: true,
    localStorage: localOk,
    cookie: cookieOk,
    // A write that "succeeds" but reads back empty means the cookie was
    // dropped/partitioned (some in-app browsers) — treat it as not durable.
    cookieReadback: cookieOk && readCookie() !== null,
    sessionStorage: sessionOk,
    account: null,
  };
  lastPersist = persist;
  // Task #6095 — fan out to the account layer when a logged-in session has
  // registered one. Fire-and-forget; the outcome is recorded in the
  // diagnostics and observable via waitForAccountPersist().
  if (accountSync) {
    lastAccountPersist = accountSync(next)
      .catch(() => false)
      .then((ok) => {
        persist.account = ok;
        return ok;
      });
  } else {
    lastAccountPersist = Promise.resolve(false);
  }
  for (const l of listeners) {
    try {
      l(next);
    } catch {}
  }
  return next;
}

export function subscribeConsent(cb: (s: ConsentState) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function openConsentSettings() {
  try {
    window.dispatchEvent(new CustomEvent("bac:open-consent"));
  } catch {}
}
